import { useEffect, useState, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { createRoot } from 'react-dom/client'
import { useAuth } from '@/contexts/AuthContext'
import { useTeacherPreview, usePreviewPath } from '@/contexts/TeacherPreviewContext'
import { supabase } from '@/lib/supabase'
import { getContent, getExercisesByContent } from '@/lib/db'
import type { Content, Exercise, Lesson, Unit } from '@/types'
import AudioPlayer from '@/components/common/AudioPlayer'
import { sanitizeHtml } from '@/lib/sanitize'
import {
  ArrowLeft,
  BookOpen,
  Play,
  Volume2,
  ChevronRight,
  Headphones,
  PenTool,
  BookOpenCheck,
  CheckCircle,
  Smartphone,
  X,
  RotateCcw,
  Settings,
  ScreenShare
} from 'lucide-react'
import EditableText from '@/components/edit/EditableText'
import EditableHtml from '@/components/edit/EditableHtml'
import { useEditMode } from '@/contexts/EditModeContext'

interface ContentWithRelations extends Content {
  lesson?: Lesson & { unit?: Unit }
}

interface ExercisesBySkill {
  listening: Exercise[]
  writing: Exercise[]
  reading: Exercise[]
}

interface CompletedBySkill {
  listening: number
  writing: number
  reading: number
}

export default function ContentView() {
  const { contentId } = useParams<{ contentId: string }>()
  const { student, isInCohort } = useAuth()
  const { isTeacherPreview } = useTeacherPreview()
  const { isEditMode } = useEditMode()
  const previewPath = usePreviewPath()
  const navigate = useNavigate()

  const [content, setContent] = useState<ContentWithRelations | null>(null)
  const [exercises, setExercises] = useState<ExercisesBySkill>({
    listening: [],
    writing: [],
    reading: []
  })
  const [completedExercises, setCompletedExercises] = useState<CompletedBySkill>({
    listening: 0,
    writing: 0,
    reading: 0
  })
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'theory' | 'exercises'>('theory')
  const [showRotateHint, setShowRotateHint] = useState(false)
  const [rotateStep, setRotateStep] = useState<'hint' | 'guide'>('hint')
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
  const theoryRef = useRef<HTMLDivElement>(null)
  const audioRootsRef = useRef<ReturnType<typeof createRoot>[]>([])

  // Show rotate-to-landscape hint on mobile portrait
  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768
    if (!isMobile) return

    const dismissed = sessionStorage.getItem('rotate-hint-dismissed')
    if (dismissed) return

    const checkOrientation = () => {
      const isPortrait = window.innerHeight > window.innerWidth
      setShowRotateHint(isPortrait)
    }

    checkOrientation()
    window.addEventListener('resize', checkOrientation)
    window.addEventListener('orientationchange', checkOrientation)

    return () => {
      window.removeEventListener('resize', checkOrientation)
      window.removeEventListener('orientationchange', checkOrientation)
    }
  }, [])

  const dismissRotateHint = () => {
    setShowRotateHint(false)
    setRotateStep('hint')
    sessionStorage.setItem('rotate-hint-dismissed', '1')
  }

  const tryForceLandscape = async () => {
    try {
      // Android: fullscreen + lock orientation
      const docEl = document.documentElement as any
      const requestFs = docEl.requestFullscreen || docEl.webkitRequestFullscreen || docEl.mozRequestFullScreen || docEl.msRequestFullscreen
      if (requestFs) {
        await requestFs.call(docEl)
      }
      const orientation = screen.orientation as any
      if (orientation?.lock) {
        await orientation.lock('landscape')
        dismissRotateHint()
        return
      }
    } catch {
      // Fullscreen or lock failed — expected on iOS
    }
    // If we get here, auto-rotate failed → show manual guide
    setRotateStep('guide')
  }

  // Replace native <audio> in theory HTML with custom AudioPlayer + fix tables for mobile
  useEffect(() => {
    const container = theoryRef.current
    if (!container) return
    // Cleanup previous roots
    audioRootsRef.current.forEach(root => root.unmount())
    audioRootsRef.current = []
    // Find all native audio elements
    const audios = container.querySelectorAll('audio')
    audios.forEach(audio => {
      const src = audio.getAttribute('src') || audio.querySelector('source')?.getAttribute('src')
      if (!src) return
      // Create a wrapper div to mount the React AudioPlayer
      const wrapper = document.createElement('div')
      wrapper.style.width = '100%'
      wrapper.style.margin = '8px 0'
      audio.replaceWith(wrapper)
      const root = createRoot(wrapper)
      root.render(<AudioPlayer src={src} />)
      audioRootsRef.current.push(root)
    })

    // Wrap bare tables in a scrollable container for mobile
    const tables = container.querySelectorAll('table')
    tables.forEach(table => {
      if (table.parentElement?.classList.contains('table-scroll-wrapper')) return
      const wrapper = document.createElement('div')
      wrapper.className = 'table-scroll-wrapper'
      wrapper.style.overflowX = 'auto'
      ;(wrapper.style as any).webkitOverflowScrolling = 'touch'
      wrapper.style.margin = '1rem 0'
      table.parentNode?.insertBefore(wrapper, table)
      wrapper.appendChild(table)
    })

    // Fix elements with fixed width inline styles
    const fixedWidthEls = container.querySelectorAll('[style*="width"]')
    fixedWidthEls.forEach(el => {
      const htmlEl = el as HTMLElement
      const w = htmlEl.style.width
      if (w && w.includes('px') && Number.parseInt(w) > 400) {
        htmlEl.style.maxWidth = '100%'
        htmlEl.style.width = '100%'
      }
    })

    // Fix two-column layouts: hide empty children, make lone last child span full width
    const layoutContainers = container.querySelectorAll('[style*="grid"], [style*="flex"]')
    layoutContainers.forEach(layoutEl => {
      const children = Array.from(layoutEl.children) as HTMLElement[]
      // Hide visually empty children (no text, no media)
      children.forEach(child => {
        const text = (child.textContent || '').replace(/\u00a0/g, '').trim()
        const hasMedia = child.querySelectorAll('img, video, audio, iframe, svg, canvas').length > 0
        if (!text && !hasMedia) {
          child.style.display = 'none'
        }
      })
      // Make last visible child span full width if it's alone in its row (grid)
      const visibleChildren = children.filter(c => c.style.display !== 'none')
      const el = layoutEl as HTMLElement
      if (visibleChildren.length > 0 && visibleChildren.length % 2 === 1) {
        const lastChild = visibleChildren[visibleChildren.length - 1]
        if (el.style.display?.includes('grid') || el.style.cssText?.includes('grid')) {
          lastChild.style.gridColumn = '1 / -1'
        } else if (el.style.display?.includes('flex') || el.style.cssText?.includes('flex')) {
          lastChild.style.width = '100%'
          lastChild.style.flex = '0 0 100%'
        }
      }
    })

    return () => {
      audioRootsRef.current.forEach(root => root.unmount())
      audioRootsRef.current = []
    }
  }, [content?.theory_content])

  useEffect(() => {
    if (contentId) {
      loadContent()
    }
  }, [contentId])

  const isPremium = student?.subscription_status === 'active'
  const hasFullAccess = isPremium && isInCohort

  const loadContent = async () => {
    if (!contentId) return

    setIsLoading(true)
    try {
      // Cargar contenido con relaciones (usa mapContent/mapLesson para campos correctos)
      const { data: contentData, error: contentError } = await getContent(contentId)

      if (contentError) throw contentError

      // Premium gate: check if content belongs to free lesson (no aplica en previsualización)
      if (!hasFullAccess && !isTeacherPreview) {
        const rel = contentData as ContentWithRelations
        const unitOrder = rel?.lesson?.unit?.order_num
        const lessonOrder = rel?.lesson?.order_num
        if (unitOrder !== 1 || lessonOrder !== 1) {
          navigate('/units', { replace: true })
          return
        }
      }

      setContent(contentData as ContentWithRelations)

      // Cargar ejercicios (usa order_index correcto)
      const { data: exercisesData, error: exercisesError } = await getExercisesByContent(contentId)

      if (exercisesError) throw exercisesError

      // Organizar ejercicios por habilidad
      const exercisesBySkill: ExercisesBySkill = {
        listening: [],
        writing: [],
        reading: []
      }

      exercisesData?.forEach((exercise: Exercise) => {
        if (exercise.skill && exercise.skill !== 'speaking' && exercisesBySkill[exercise.skill as keyof ExercisesBySkill]) {
          exercisesBySkill[exercise.skill as keyof ExercisesBySkill].push(exercise)
        }
      })

      setExercises(exercisesBySkill)

      // Cargar progreso de ejercicios completados (no aplica en previsualización)
      if (student && !isTeacherPreview) {
        const exerciseIds = exercisesData?.map(e => e.id) || []
        if (exerciseIds.length > 0) {
          const { data: resultsData } = await supabase
            .from('exercise_results')
            .select('exercise_id, is_correct')
            .eq('student_id', student.id)
            .in('exercise_id', exerciseIds)
            .eq('is_correct', true)

          // Contar completados por habilidad
          const completed: CompletedBySkill = {
            listening: 0,
            writing: 0,
            reading: 0
          }

          const results = resultsData as { exercise_id: string; is_correct: boolean }[] | null
          results?.forEach(result => {
            const exercise = exercisesData?.find(e => e.id === result.exercise_id)
            if (exercise?.skill && exercise.skill !== 'speaking' && completed[exercise.skill as keyof CompletedBySkill] !== undefined) {
              completed[exercise.skill as keyof CompletedBySkill]++
            }
          })

          setCompletedExercises(completed)
        }
      }
    } catch (error) {
      console.error('Error loading content:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Iniciar ejercicios desde el primero (listening -> writing -> reading)
  const startAllExercises = () => {
    // Ordenar: listening primero, luego writing, luego reading
    const allExercises = [
      ...exercises.listening,
      ...exercises.writing,
      ...exercises.reading
    ]
    if (allExercises.length > 0) {
      navigate(previewPath(`/exercise/${allExercises[0].id}`))
    }
  }

  const getTotalExercises = () => {
    return exercises.listening.length + exercises.writing.length + exercises.reading.length
  }

  const getTotalCompleted = () => {
    return completedExercises.listening + completedExercises.writing + completedExercises.reading
  }

  const isContentFullyCompleted = () => {
    return getTotalExercises() > 0 && getTotalCompleted() >= getTotalExercises()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (!content) {
    return (
      <div className="text-center py-12">
        <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Contenido no encontrado</p>
        <Link to={previewPath('/units')} className="text-blue-600 hover:underline text-sm mt-2 inline-block">
          Volver a unidades
        </Link>
      </div>
    )
  }

  const skillInfo = [
    { key: 'listening', label: 'Escucha', icon: Headphones, bgColor: 'bg-blue-100', textColor: 'text-blue-600', count: exercises.listening.length, completed: completedExercises.listening },
    { key: 'writing', label: 'Escritura', icon: PenTool, bgColor: 'bg-green-100', textColor: 'text-green-600', count: exercises.writing.length, completed: completedExercises.writing },
    { key: 'reading', label: 'Lectura', icon: BookOpenCheck, bgColor: 'bg-orange-100', textColor: 'text-orange-600', count: exercises.reading.length, completed: completedExercises.reading }
  ]

  return (
    <div className="min-h-screen flex flex-col" translate="no" lang="fr">
      {/* Hint: rotar pantalla en móvil */}
      {showRotateHint && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 animate-fadeIn">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center shadow-2xl relative">
            <button
              onClick={dismissRotateHint}
              className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {rotateStep === 'hint' ? (
              <>
                <div className="mb-4 flex justify-center">
                  <div className="rotate-phone-icon">
                    <Smartphone className="w-14 h-14 text-blue-600" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Gira tu pantalla
                </h3>
                <p className="text-sm text-gray-500 mb-5">
                  El contenido se ve mucho mejor en horizontal.
                </p>
                <button
                  onClick={tryForceLandscape}
                  className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <RotateCcw className="w-5 h-5" />
                  Activar vista horizontal
                </button>
                <button
                  onClick={dismissRotateHint}
                  className="w-full mt-2 py-2 text-gray-400 text-sm hover:text-gray-600 transition-colors"
                >
                  Omitir
                </button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {isIOS ? 'Cómo activar la rotación' : 'Activa la rotación manual'}
                </h3>
                {isIOS ? (
                  <div className="space-y-4 text-left">
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                      <p className="text-sm text-gray-600">Desliza hacia abajo desde la <strong>esquina superior derecha</strong> para abrir el Centro de Control</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                      <div className="text-sm text-gray-600">
                        <p>Busca el icono del <strong>candado con flecha circular</strong> y asegúrate de que esté <strong>desactivado</strong> (sin color)</p>
                        <div className="mt-2 flex justify-center">
                          <div className="inline-flex items-center gap-3">
                            <div className="flex flex-col items-center">
                              <div className="w-10 h-10 rounded-lg bg-red-100 border-2 border-red-300 flex items-center justify-center">
                                <ScreenShare className="w-5 h-5 text-red-500" />
                              </div>
                              <span className="text-xs text-red-500 mt-1">Bloqueado</span>
                            </div>
                            <span className="text-gray-300 text-lg">→</span>
                            <div className="flex flex-col items-center">
                              <div className="w-10 h-10 rounded-lg bg-green-100 border-2 border-green-300 flex items-center justify-center">
                                <ScreenShare className="w-5 h-5 text-green-500" />
                              </div>
                              <span className="text-xs text-green-600 mt-1 font-medium">Desbloqueado</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                      <p className="text-sm text-gray-600">Gira tu teléfono de lado y la pantalla rotará automáticamente</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 text-left">
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                      <p className="text-sm text-gray-600">Desliza hacia abajo desde la <strong>parte superior</strong> para abrir el panel de ajustes rápidos</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                      <div className="text-sm text-gray-600">
                        <p>Busca <strong>"Rotación automática"</strong> y actívala</p>
                        <div className="mt-2 flex justify-center">
                          <div className="inline-flex items-center gap-3">
                            <div className="flex flex-col items-center">
                              <div className="w-10 h-10 rounded-lg bg-gray-100 border-2 border-gray-300 flex items-center justify-center">
                                <Settings className="w-5 h-5 text-gray-400" />
                              </div>
                              <span className="text-xs text-gray-400 mt-1">Apagado</span>
                            </div>
                            <span className="text-gray-300 text-lg">→</span>
                            <div className="flex flex-col items-center">
                              <div className="w-10 h-10 rounded-lg bg-green-100 border-2 border-green-300 flex items-center justify-center">
                                <Settings className="w-5 h-5 text-green-500" />
                              </div>
                              <span className="text-xs text-green-600 mt-1 font-medium">Encendido</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                      <p className="text-sm text-gray-600">Gira tu teléfono de lado y listo</p>
                    </div>
                  </div>
                )}

                <button
                  onClick={dismissRotateHint}
                  className="w-full mt-5 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                >
                  Entendido
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {/* Header fijo - barra superior compacta */}
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 sm:px-4 md:px-8 py-2 sm:py-3 gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Link
              to={content.lesson ? previewPath(`/lesson/${content.lesson_id}`) : previewPath('/units')}
              className="p-1.5 sm:p-2 hover:bg-muted rounded-lg transition-colors flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            {content.lesson && (
              <div className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-muted-foreground flex-shrink-0">
                <span>U{content.lesson.unit?.order_num}</span>
                <ChevronRight className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span>L{content.lesson.order_num}</span>
                <ChevronRight className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              </div>
            )}
            <EditableText
              as="h1"
              table="contents"
              id={content.id}
              field="title"
              value={content.title}
              placeholder="(sin título)"
              className="text-base sm:text-lg font-bold text-foreground truncate min-w-0"
              onSaved={(v) => setContent({ ...content, title: v } as ContentWithRelations)}
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-muted p-1 rounded-lg self-stretch sm:self-auto flex-shrink-0">
            <button
              onClick={() => setActiveTab('theory')}
              className={`flex-1 sm:flex-initial py-1.5 sm:py-2 px-3 sm:px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'theory'
                  ? 'bg-background text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <BookOpen className="w-4 h-4 inline mr-1" />
              Teoría
            </button>
            <button
              onClick={() => setActiveTab('exercises')}
              className={`flex-1 sm:flex-initial py-1.5 sm:py-2 px-3 sm:px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'exercises'
                  ? 'bg-background text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <PenTool className="w-4 h-4 inline mr-1" />
              Ejercicios ({getTotalExercises()})
            </button>
          </div>
        </div>
      </header>

      {/* Contenido principal - ocupa todo el espacio restante */}
      <div className="flex-1">
        {/* Theory Tab */}
        {activeTab === 'theory' && (
          <div>
            {/* Video - ancho completo */}
            {content.video_url && (
              <div className="bg-black">
                <div className="aspect-video max-h-[75vh] mx-auto">
                  {content.video_url.includes('youtube') || content.video_url.includes('youtu.be') ? (
                    <iframe
                      src={content.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      className="w-full h-full"
                      allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    />
                  ) : (
                    <video
                      src={content.video_url}
                      controls
                      className="w-full h-full"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Audio - barra compacta */}
            {content.audio_url && (
              <div className="px-3 sm:px-6 md:px-12 py-3 bg-muted/50 border-b">
                <div className="flex items-center gap-3 max-w-4xl mx-auto">
                  <Volume2 className="w-5 h-5 text-blue-600 shrink-0" />
                  <AudioPlayer src={content.audio_url} className="w-full" />
                </div>
              </div>
            )}

            {/* Contenido teórico - ancho completo con prose grande */}
            {(() => {
              const theoryText = content.theory_content || ''

              if (theoryText) {
                const isHTML = theoryText.trim().startsWith('<') || /<[a-z][\s\S]*>/i.test(theoryText)

                if (isHTML) {
                  // En modo edición: editor inline sin el post-procesamiento de audios/tablas
                  // (esos efectos rompen el contentEditable). El render normal vuelve cuando
                  // se apaga el modo edición.
                  if (isEditMode) {
                    return (
                      <div className="px-3 sm:px-4 md:px-8 lg:px-12 py-4 sm:py-8 overflow-x-hidden">
                        <EditableHtml
                          table="contents"
                          id={content.id}
                          field="theory_content"
                          value={content.theory_content}
                          className="theory-content prose prose-sm sm:prose-lg lg:prose-xl max-w-none"
                          renderHtml={(html) => (
                            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
                          )}
                          onSaved={(v) => setContent({ ...content, theory_content: v } as ContentWithRelations)}
                        />
                      </div>
                    )
                  }
                  return (
                    <div className="px-3 sm:px-4 md:px-8 lg:px-12 py-4 sm:py-8 overflow-x-hidden">
                      <div
                        ref={theoryRef}
                        className="theory-content prose prose-sm sm:prose-lg lg:prose-xl max-w-none
                          prose-headings:text-gray-900 prose-headings:font-bold
                          prose-h1:text-3xl lg:prose-h1:text-4xl prose-h1:text-blue-600 prose-h1:border-b prose-h1:border-blue-200 prose-h1:pb-3 prose-h1:mb-6
                          prose-h2:text-2xl lg:prose-h2:text-3xl prose-h2:text-blue-500 prose-h2:mt-8 prose-h2:mb-4
                          prose-h3:text-xl lg:prose-h3:text-2xl prose-h3:text-gray-800 prose-h3:mt-6 prose-h3:mb-3
                          prose-p:text-gray-700 prose-p:leading-relaxed prose-p:my-4
                          prose-a:text-blue-600 prose-a:underline hover:prose-a:text-blue-800
                          prose-strong:text-gray-900 prose-strong:font-semibold
                          prose-em:italic prose-em:text-gray-600
                          prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
                          prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6
                          prose-li:text-gray-700 prose-li:my-1.5
                          prose-blockquote:border-l-4 prose-blockquote:border-blue-400 prose-blockquote:bg-blue-50 prose-blockquote:pl-4 prose-blockquote:py-3 prose-blockquote:my-6 prose-blockquote:italic prose-blockquote:text-gray-600 prose-blockquote:rounded-r-lg
                          prose-table:w-full prose-table:my-6 prose-table:border-collapse
                          prose-th:bg-blue-100 prose-th:text-blue-800 prose-th:font-semibold prose-th:p-3 prose-th:text-left prose-th:border prose-th:border-blue-200
                          prose-td:p-3 prose-td:border prose-td:border-gray-200
                          prose-tr:even:bg-gray-50
                          prose-img:rounded-lg prose-img:shadow-md prose-img:my-6
                          prose-code:bg-gray-100 prose-code:text-pink-600 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
                          prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto
                        "
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(theoryText) }}
                      />
                    </div>
                  )
                }

                // Fallback: Convertir markdown básico a React
                const formatContent = (text: string) => {
                  return text
                    .split('\n')
                    .map((line, index) => {
                      if (line.startsWith('### ')) {
                        return <h3 key={index} className="text-xl font-semibold text-gray-800 mt-6 mb-3">{line.replace('### ', '')}</h3>
                      }
                      if (line.startsWith('## ')) {
                        return <h2 key={index} className="text-2xl font-bold text-gray-900 mt-8 mb-4">{line.replace('## ', '')}</h2>
                      }
                      if (line.startsWith('# ')) {
                        return <h1 key={index} className="text-3xl font-bold text-blue-600 mb-6">{line.replace('# ', '')}</h1>
                      }
                      if (line.startsWith('- **')) {
                        const match = line.match(/- \*\*(.+?)\*\* - (.+)/)
                        if (match) {
                          return (
                            <div key={index} className="flex gap-3 py-2 text-lg">
                              <span className="text-blue-500">•</span>
                              <span><strong className="text-gray-900">{match[1]}</strong> - {match[2]}</span>
                            </div>
                          )
                        }
                      }
                      if (line.startsWith('- ')) {
                        return (
                          <div key={index} className="flex gap-3 py-2 text-lg">
                            <span className="text-blue-500">•</span>
                            <span>{line.replace('- ', '')}</span>
                          </div>
                        )
                      }
                      if (line.startsWith('|') && line.includes('|')) {
                        const cells = line.split('|').filter(c => c.trim())
                        if (line.includes('---')) return null
                        return (
                          <div key={index} className="grid grid-cols-2 gap-3 py-2 border-b border-gray-200 text-lg">
                            {cells.map((cell, i) => (
                              <span key={i} className={i === 0 ? 'font-medium text-blue-600' : 'text-gray-600'}>
                                {cell.trim()}
                              </span>
                            ))}
                          </div>
                        )
                      }
                      if (line.startsWith('> ')) {
                        return (
                          <div key={index} className="pl-5 border-l-4 border-blue-300 py-2 italic text-gray-600 text-lg my-4">
                            {line.replace('> ', '')}
                          </div>
                        )
                      }
                      if (line.trim() === '') {
                        return <div key={index} className="h-3" />
                      }
                      return <p key={index} className="text-gray-700 py-2 text-lg leading-relaxed">{line}</p>
                    })
                }

                return (
                  <div className="px-3 sm:px-6 md:px-12 lg:px-20 py-4 sm:py-8">
                    <div className="space-y-1">
                      {formatContent(theoryText)}
                    </div>
                  </div>
                )
              }
              return null
            })()}

            {/* Sin contenido */}
            {!content.video_url && !content.audio_url && !content.theory_content && (
              <div className="py-20 text-center">
                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No hay contenido teórico disponible</p>
                <button
                  onClick={() => setActiveTab('exercises')}
                  className="mt-4 text-blue-600 hover:underline text-lg"
                >
                  Ir a los ejercicios
                </button>
              </div>
            )}

            {/* Botón continuar a ejercicios */}
            {getTotalExercises() > 0 && (
              <div className="flex justify-center px-3 sm:px-0 py-6 sm:py-8 border-t">
                <button
                  onClick={() => setActiveTab('exercises')}
                  className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-base sm:text-lg shadow-lg w-full sm:w-auto justify-center"
                >
                  Continuar a los ejercicios
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Exercises Tab */}
        {activeTab === 'exercises' && (
          <div className="px-3 sm:px-6 md:px-12 lg:px-20 py-4 sm:py-8 space-y-6 sm:space-y-8">
            {getTotalExercises() === 0 ? (
              <div className="py-20 text-center">
                <PenTool className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No hay ejercicios disponibles para este contenido</p>
              </div>
            ) : (
              <>
                {/* Estado del contenido */}
                <div className={`rounded-xl p-4 sm:p-8 border-2 max-w-3xl mx-auto ${isContentFullyCompleted() ? 'border-green-400 bg-green-50/50' : 'border-border bg-card'}`}>
                  {isContentFullyCompleted() ? (
                    <div className="text-center">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-green-100 mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                        <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-green-600 mb-2">Contenido Completado</h3>
                      <p className="text-muted-foreground mb-4 sm:mb-6 text-base sm:text-lg">
                        Has completado todos los {getTotalExercises()} ejercicios de este contenido.
                      </p>
                      <button
                        onClick={startAllExercises}
                        className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-green-100 text-green-700 rounded-xl hover:bg-green-200 transition-colors font-medium text-base sm:text-lg"
                      >
                        <Play className="w-5 h-5" />
                        Repetir ejercicios
                      </button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-blue-100 mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                        <PenTool className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold mb-2">Ejercicios del Contenido</h3>
                      <p className="text-muted-foreground mb-4 sm:mb-6 text-base sm:text-lg">
                        Completa los {getTotalExercises()} ejercicios para avanzar al siguiente contenido.
                      </p>

                      {getTotalCompleted() > 0 && (
                        <div className="mb-6 max-w-md mx-auto">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-muted-foreground">Tu progreso</span>
                            <span className="font-bold text-blue-600">{getTotalCompleted()} / {getTotalExercises()}</span>
                          </div>
                          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500 rounded-full transition-all"
                              style={{ width: `${(getTotalCompleted() / getTotalExercises()) * 100}%` }}
                            />
                          </div>
                        </div>
                      )}

                      <button
                        onClick={startAllExercises}
                        className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-base sm:text-lg shadow-lg"
                      >
                        <Play className="w-5 h-5 sm:w-6 sm:h-6" />
                        {getTotalCompleted() > 0 ? 'Continuar ejercicios' : 'Comenzar ejercicios'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Resumen de habilidades */}
                <div className="max-w-3xl mx-auto">
                  <h4 className="font-semibold mb-4 text-lg">Ejercicios por habilidad</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    {skillInfo.map((skill) => {
                      const Icon = skill.icon
                      const isComplete = skill.count > 0 && skill.completed >= skill.count

                      return (
                        <div
                          key={skill.key}
                          className={`p-3 sm:p-5 rounded-xl ${isComplete ? 'bg-green-50 border border-green-200' : `${skill.bgColor} border border-transparent`}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            {isComplete ? (
                              <CheckCircle className="w-6 h-6 text-green-600" />
                            ) : (
                              <Icon className={`w-6 h-6 ${skill.textColor}`} />
                            )}
                            <span className={`font-medium text-lg ${isComplete ? 'text-green-700' : skill.textColor}`}>
                              {skill.label}
                            </span>
                          </div>
                          <p className={`text-sm ${isComplete ? 'text-green-600' : 'text-gray-600'}`}>
                            {isComplete
                              ? `${skill.count}/${skill.count} completados`
                              : skill.completed > 0
                                ? `${skill.completed}/${skill.count} completados`
                                : `${skill.count} ejercicios`}
                          </p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
